import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpointAPI } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface Threat {
  id: number;
  endpoint_id: number;
  endpoint_name: string;
  os: string;
  file_path: string;
  signature_name?: string;
  severity: string;
  source: string;
  action: string;
  status: string;
  detected_at: string;
}

interface Counts { critical: number; high: number; medium: number; low: number; total: number; }

const sevBadge = (s: string) =>
  s === 'critical' ? 'bg-danger'
  : s === 'high' ? 'bg-warning text-dark'
  : s === 'medium' ? 'bg-info text-dark'
  : 'bg-secondary';

const EndpointThreatsPage: React.FC = () => {
  useDocumentTitle('Threat Center');
  const navigate = useNavigate();
  const [threats, setThreats] = useState<Threat[]>([]);
  const [counts, setCounts] = useState<Counts>({ critical: 0, high: 0, medium: 0, low: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await endpointAPI.threatFeed(statusFilter);
      if (res.data.status === 'success') {
        setThreats(res.data.data.threats || []);
        setCounts(res.data.data.counts || { critical: 0, high: 0, medium: 0, low: 0, total: 0 });
      }
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [statusFilter]);

  return (
    <DashboardLayout>
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h3 className="mb-1"><i className="bi bi-shield-exclamation me-2"></i>Threat Center</h3>
            <p className="text-muted mb-0">All threats detected across your endpoints, in one place.</p>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/endpoints')}>
            <i className="bi bi-pc-display me-2"></i>All Endpoints
          </button>
        </div>

        {/* Severity summary */}
        <div className="row g-3 mb-4">
          {[
            { label: 'Critical', value: counts.critical, color: 'text-danger', icon: 'bi-exclamation-octagon' },
            { label: 'High', value: counts.high, color: 'text-warning', icon: 'bi-exclamation-triangle' },
            { label: 'Medium', value: counts.medium, color: 'text-info', icon: 'bi-info-circle' },
            { label: 'Open total', value: counts.total, color: 'text-primary', icon: 'bi-collection' },
          ].map(c => (
            <div className="col-6 col-md-3" key={c.label}>
              <div className="card h-100"><div className="card-body d-flex align-items-center justify-content-between">
                <div><div className={`fs-3 fw-bold ${c.color}`}>{c.value}</div><div className="text-muted small">{c.label}</div></div>
                <i className={`bi ${c.icon} fs-2 ${c.color} opacity-50`}></i>
              </div></div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="btn-group mb-3">
          {['open', 'resolved', 'all'].map(f => (
            <button key={f} className={`btn btn-sm ${statusFilter === f ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setStatusFilter(f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}
        </div>

        <div className="card">
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
            ) : threats.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-shield-check fs-1 text-success d-block mb-2"></i>
                No {statusFilter !== 'all' ? statusFilter : ''} threats across your fleet.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Severity</th><th>Endpoint</th><th>File</th><th>Signature</th><th>Detected</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {threats.map(t => (
                      <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/endpoints/${t.endpoint_id}`)}>
                        <td><span className={`badge ${sevBadge(t.severity)}`}>{t.severity}</span></td>
                        <td><i className={`bi ${t.os?.includes('win') ? 'bi-windows' : t.os?.includes('mac') || t.os?.includes('darwin') ? 'bi-apple' : 'bi-ubuntu'} me-2`}></i>{t.endpoint_name}</td>
                        <td className="text-truncate" style={{ maxWidth: 260 }} title={t.file_path}><code className="small">{t.file_path}</code></td>
                        <td className="small">{t.signature_name || '—'}</td>
                        <td className="small text-muted">{new Date(t.detected_at).toLocaleString()}</td>
                        <td className="text-end"><i className="bi bi-chevron-right text-muted"></i></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EndpointThreatsPage;
